import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Alert,
  Tooltip,
  useTheme
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { useAuthStore } from '../../store/authStore';

/**
 * Admin page for managing promotions
 */
const Promotions = () => {
  const theme = useTheme();
  const { user } = useAuthStore();
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [promotionToDelete, setPromotionToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // Fetch promotions with pagination
  useEffect(() => {
    const fetchPromotions = async () => {
      // Проверяем наличие токена перед запросом
      const token = localStorage.getItem('token');
      if (!user || !token) {
        console.error('Отсутствует токен авторизации, перенаправление на страницу входа');
        setError('Нет токена авторизации. Перенаправление на страницу входа...');
        setLoading(false);
        
        // Перенаправляем на страницу входа с задержкой
        setTimeout(() => {
          window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname);
        }, 2000);
        return;
      }
      
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        console.log('Fetching promotions with token:', token.substring(0, 15) + '...');
        
        const response = await axios.get('/api/promotions', {
          params: {
            page: page + 1, // API uses 1-based indexing
            limit: rowsPerPage
          },
          headers: {
            Authorization: `Bearer ${token}`
          },
          // Добавляем таймаут для запроса
          timeout: 10000 // 10 секунд
        });
        
        console.log('Promotions API response:', response.data);
        
        if (response.data && Array.isArray(response.data.promotions)) {
          // Добавляем отладочный вывод для первой акции
          if (response.data.promotions.length > 0) {
            console.log('Структура объекта акции:', {
              id: response.data.promotions[0].id,
              _id: response.data.promotions[0]._id,
              всеПоля: Object.keys(response.data.promotions[0])
            });
          }
          
          // Проверяем и исправляем данные акций
          const normalizedPromotions = response.data.promotions.map(promotion => {
            // Если есть id, но нет _id, копируем id в _id
            if (promotion.id && !promotion._id) {
              promotion._id = promotion.id;
              console.log('Исправлено: скопирован id в _id для акции:', promotion.title);
            }
            // Если есть _id, но нет id, копируем _id в id
            if (promotion._id && !promotion.id) {
              promotion.id = promotion._id;
              console.log('Исправлено: скопирован _id в id для акции:', promotion.title);
            }
            return promotion;
          });
          
          setPromotions(normalizedPromotions);
          setTotalCount(response.data.totalCount || 0);
          setError(null);
        } else {
          console.error('Invalid API response format:', response.data);
          setError('Некорректный формат ответа от API');
          setPromotions([]);
        }
      } catch (err) {
        console.error('Error fetching promotions:', err);
        console.error('Error details:', err.response ? err.response.data : 'No response data');
        setError(`Ошибка загрузки акций: ${err.message}`);
        setPromotions([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPromotions();
  }, [page, rowsPerPage, user]);

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Open delete confirmation dialog
  const handleOpenDeleteDialog = (promotion) => {
    setPromotionToDelete(promotion);
    setDeleteDialogOpen(true);
    setDeleteError(null);
  };

  // Close delete confirmation dialog
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setPromotionToDelete(null);
    setDeleteError(null);
  };

  // Delete promotion
  const handleDeletePromotion = async () => {
    if (!promotionToDelete) {
      console.error('No promotion selected for deletion');
      setDeleteError('Не выбрана акция для удаления');
      return;
    }
    
    // Проверяем наличие ID
    if (!promotionToDelete._id || promotionToDelete._id === 'undefined' || promotionToDelete._id === 'null') {
      console.error('Некорректный ID акции для удаления:', promotionToDelete._id);
      setDeleteError('Некорректный ID акции для удаления');
      return;
    }
    
    // Проверяем наличие токена
    const token = localStorage.getItem('token');
    if (!user || !token) {
      console.error('Отсутствует токен авторизации, невозможно удалить акцию');
      setDeleteError('Нет токена авторизации. Пожалуйста, войдите в систему снова.');
      return;
    }
    
    try {
      setDeleteLoading(true);
      console.log(`Удаление акции с ID: ${promotionToDelete._id}`);
      
      await axios.delete(`/api/promotions/${promotionToDelete._id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        timeout: 10000 // 10 секунд таймаут
      });
      
      console.log(`Акция ${promotionToDelete._id} успешно удалена`);
      
      // Обновляем список акций
      setPromotions(promotions.filter(p => p._id !== promotionToDelete._id));
      setTotalCount(prev => prev - 1);
      handleCloseDeleteDialog();
    } catch (err) {
      console.error('Error deleting promotion:', err);
      console.error('Error details:', err.response ? err.response.data : 'No response data');
      setDeleteError(`Ошибка при удалении акции: ${err.message}`);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Не указано';
    try {
      return format(new Date(dateString), 'dd MMMM yyyy', { locale: ru });
    } catch (error) {
      return 'Неверная дата';
    }
  };

  // Get status chip based on active status and dates
  const getStatusChip = (promotion) => {
    const now = new Date();
    const startDate = promotion.startDate ? new Date(promotion.startDate) : null;
    const endDate = promotion.endDate ? new Date(promotion.endDate) : null;
    
    if (!promotion.isActive) {
      return (
        <Chip 
          label="Неактивна" 
          color="default" 
          size="small" 
          sx={{ backgroundColor: theme.palette.grey[300] }}
        />
      );
    }
    
    if (startDate && now < startDate) {
      return (
        <Chip 
          label="Запланирована" 
          color="info" 
          size="small" 
        />
      );
    }
    
    if (endDate && now > endDate) {
      return (
        <Chip 
          label="Завершена" 
          color="warning" 
          size="small" 
        />
      );
    }
    
    return (
      <Chip 
        label="Активна" 
        color="success" 
        size="small" 
      />
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Управление акциями
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          component={RouterLink}
          to="/admin/promotions/new"
        >
          Добавить акцию
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 250px)' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Название</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Начало</TableCell>
                <TableCell>Окончание</TableCell>
                <TableCell>Приоритет</TableCell>
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : promotions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1">
                      Акции не найдены
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                promotions.map((promotion) => (
                  <TableRow key={promotion._id} hover>
                    <TableCell>
                      <Typography variant="body1" fontWeight="medium">
                        {promotion.title}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {getStatusChip(promotion)}
                    </TableCell>
                    <TableCell>
                      {formatDate(promotion.startDate)}
                    </TableCell>
                    <TableCell>
                      {formatDate(promotion.endDate)}
                    </TableCell>
                    <TableCell>
                      {promotion.priority || 0}
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Tooltip title="Просмотр">
                          <IconButton 
                            component={RouterLink}
                            to={`/admin/promotions/preview/${promotion._id || promotion.id || ''}`}
                            size="small"
                            onClick={(e) => {
                              // Проверка наличия валидного ID перед переходом
                              const promotionId = promotion._id || promotion.id;
                              if (!promotionId) {
                                e.preventDefault();
                                console.error('Отсутствует ID акции');
                                setError('Невозможно просмотреть акцию без ID');
                              } else {
                                console.log('Переход к просмотру акции с ID:', promotionId);
                              }
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Редактировать">
                          <IconButton 
                            component={RouterLink}
                            to={`/admin/promotions/edit/${promotion._id || promotion.id || 'new'}`}
                            size="small"
                            sx={{ ml: 1 }}
                            onClick={(e) => {
                              // Проверка наличия валидного ID перед переходом
                              const promotionId = promotion._id || promotion.id;
                              if (!promotionId) {
                                e.preventDefault();
                                console.error('Отсутствует ID акции');
                                setError('Невозможно редактировать акцию без ID');
                              } else {
                                console.log('Переход к редактированию акции с ID:', promotionId);
                              }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Удалить">
                          <IconButton 
                            onClick={() => handleOpenDeleteDialog(promotion)}
                            size="small"
                            sx={{ ml: 1 }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Строк на странице:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} из ${count !== -1 ? count : 'более чем ' + to}`}
        />
      </Paper>
      
      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
      >
        <DialogTitle>Подтверждение удаления</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Вы действительно хотите удалить акцию "{promotionToDelete?.title}"? Это действие нельзя отменить.
          </DialogContentText>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={deleteLoading}>
            Отмена
          </Button>
          <Button 
            onClick={handleDeletePromotion} 
            color="error" 
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={20} /> : null}
          >
            Удалить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Promotions;
